import {
  Component,
  OnInit,
  OnDestroy,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';

import {
  IrisIconComponent,
  IconColors,
  IconSizes,
  IconUsageTypes
} from '@sondermind/iris/icon';

import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { BreakpointsService } from '@sondermind/utilities/services';
import { ToastPosition } from '../../models/toast-position';
import { ToastType } from '../../models/toast-type';
import { INotificationToastInstance } from '../../models/toast-instance.interface';
import { INotificationToastConfig } from '../../models/toast-config';
import { NotificationToastRef } from '../../models/toast-ref';

/**
 * Component for a toast notification that appears on top of a page at the side
 *
 * Can be configured to disappear after a set duration, or dismissed manually
 *
 * Has an icon to the left, text in the center, and a close button on the right
 *
 * Default shows an "info" icon, pass in ToastType.NO_ICON to remove icon.
 */
@Component({
  selector: 'notification-toast',
  standalone: true,
  imports: [
    CommonModule,
    IrisIconButtonModule,
    IrisIconComponent,
  ],
  templateUrl: './notification-toast.component.html',
  styleUrl: './notification-toast.component.scss',
  animations: [
    trigger('slideAnimation', [
      state('entering', style({
        transform: '{{enterTransform}}',
        opacity: 0
      }), { params: { enterTransform: 'translateX(100%)' } }),
      state('visible', style({
        transform: '{{visibleTransform}}',
        opacity: 1
      }), { params: { visibleTransform: 'translateX(0)' } }),
      state('leaving', style({
        transform: '{{exitTransform}}',
        opacity: 0
      }), { params: { exitTransform: 'translateX(100%)' } }),
      transition('entering => visible', animate('300ms ease-in')),
      transition('visible => leaving', animate('300ms ease-out'))
    ],)
  ]
})
export class NotificationToastComponent implements OnInit, OnDestroy, INotificationToastInstance {
  // Animation state management
  animationState: 'entering' | 'visible' | 'leaving' = 'entering';

  config: INotificationToastConfig = {};

  message: string = '';
  iconName: string = '';
  iconColorToken: string = '';
  position: ToastPosition = ToastPosition.BOTTOM_RIGHT;
  type: ToastType = ToastType.INFO;
  duration: number = 5000; // milliseconds
  showCloseButton: boolean = true;
  autoClose: boolean = true;

  private timeoutId?: number;

  readonly ToastPosition = ToastPosition;
  readonly ToastType = ToastType;
  readonly IconColors = IconColors;
  readonly IconSizes = IconSizes;
  readonly IconUsageTypes = IconUsageTypes;
  readonly IrisIconButtonTypes = IrisIconButtonTypes;

  readonly positionClasses = signal<string>('');
  readonly animationParams = signal<{
    enterTransform: string;
    exitTransform: string;
    visibleTransform: string;
  }>({
    enterTransform: 'translateX(100%)',
    exitTransform: 'translateX(100%)',
    visibleTransform: 'translateX(0)'
  });

  constructor(
    private ref: NotificationToastRef,
    private breakpointService: BreakpointsService,
  ) {}

  ngOnInit(): void {
    const configData = this.ref.config;

    this.message = configData?.message ?? this.message;

    this.type = configData?.type ?? this.type;
    this.position = configData?.position ?? this.position;

    this.iconColorToken = this.getIconColor(configData?.iconColorToken);
    this.iconName = this.getIconName(configData?.iconName);

    this.duration = configData?.duration ?? this.duration;
    this.showCloseButton = configData?.showCloseButton ?? this.showCloseButton;
    this.autoClose = configData?.autoClose ?? this.autoClose;

    if (this.autoClose && this.duration > 0) {
      this.timeoutId = window.setTimeout(() => {
        this.close();
      }, this.duration);
    }

    // On mobile, always treat the toast as being in the top-center
    if (this.breakpointService.isMobileOrTablet) {
      this.position = ToastPosition.TOP_CENTER;
    }

    this.positionClasses.set(this.getPositionClasses());
    this.animationParams.set(this.getAnimationParams());

    // Brief delay to make sure everything is configured
    setTimeout(() => {
      this.animationState = 'visible';
    }, 10);
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  close(): void {
    this.animationState = 'leaving';
    this.ref.close();
  }

  handleClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('.close-toast-button')) {
      return;
    }

    if (!this.ref.config?.onClick) {
      return;
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }

    this.ref.config.onClick();
  }

  private getPositionClasses(): string {
    return `notification-toast--${this.position} notification-toast--${this.type}`;
  }

  private getAnimationParams(): {
    enterTransform: string;
    exitTransform: string;
    visibleTransform: string;
  } {
    const isCenter = this.position === ToastPosition.TOP_CENTER || this.position === ToastPosition.BOTTOM_CENTER;

    if (isCenter) {
      const yTransform = this.position === ToastPosition.TOP_CENTER ? 'translateY(-100%)' : 'translateY(100%)';
      return {
        enterTransform: `translateX(-50%) ${yTransform}`,
        exitTransform: `translateX(-50%) ${yTransform}`,
        visibleTransform: 'translateX(-50%)'
      };
    }

    const isRight = this.position === ToastPosition.TOP_RIGHT ||
                   this.position === ToastPosition.BOTTOM_RIGHT;
    return {
      enterTransform: isRight ? 'translateX(100%)' : 'translateX(-100%)',
      exitTransform: isRight ? 'translateX(100%)' : 'translateX(-100%)',
      visibleTransform: 'translateX(0)'
    };
  }

  private getIconName(iconName: string | undefined): string {
    if (this.type === ToastType.NO_ICON) {
      return '';
    }

    if (iconName) {
      return iconName;
    }

    // Default icons based on toast type
    switch (this.type) {
      case ToastType.SUCCESS:
        return 'check_circle';
      case ToastType.ERROR:
      case ToastType.WARNING:
        return 'warning';
      case ToastType.INFO:
      default:
        return 'info';
    }
  }

  private getIconColor(iconColorToken: string | undefined): string {
    if (iconColorToken) {
      return iconColorToken;
    }

    switch (this.type) {
      case ToastType.SUCCESS:
        return '$color-message-success-base';
      case ToastType.ERROR:
        return '$color-message-error-base';
      case ToastType.WARNING:
        return '$color-message-warning-base';
      case ToastType.INFO:
      default:
        return '$color-brand-1-base';
    }
  }
}
